import * as React from 'react'
import {useEffect, useMemo, useState} from 'react'
import {Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Popover, Switch, Tooltip, Typography} from '@mui/material'
import {LoadingButton} from '@mui/lab'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import CloseIcon from '@mui/icons-material/Close'
import {useForm} from 'react-hook-form'
import {useSearchParams} from 'react-router-dom'
import _ from 'lodash'
import {apiConstants, get, post} from '../../common/constants'
import {isAdmin} from '../../helpers/ReduxHelpers'
import {FormProvider, RHFSearchableSelectField} from '../../common/hook-form'
import PageTitle from '../../components/PageTitle'
import Loader from '../../common/Loader'
import {FieldLabel, FilterField} from '../../common/FilterBar'
import MerchantDataTable from './MerchantDataTable'
import EmptyState from './shared/EmptyState'
import ConfigSearch from './shared/ConfigSearch'
import {READINESS_FIELDS} from './shared/readiness'
import {C, SX, TYPE} from './shared/tokens'

// ─────────────────────────────────────────────────────────────────────────────
//  The Merchants list is now the landing page for Merchant Setup (it used to be
//  buried under a sub-menu called "Legacy Configuration" while being the entry
//  point to everything).
//
//  It answers two questions it previously could not:
//    ?segment=d2c|ptl   which book of business  -- replaces the whole duplicated
//                       "PTL Merchant Setup" module, since PTL is a property of
//                       the merchant (isPTLMerchant), not a separate product
//    ?kyc=approved|...  KYC state -- so KYC & Verification -> Approved lands
//                       here filtered instead of dumping the unfiltered list
//
//  Both are client-side filters over the list already being fetched. No new API.
// ─────────────────────────────────────────────────────────────────────────────

const SEGMENTS = {
    d2c: {label: 'D2C merchants', match: (m) => !!m.isD2CMerchant},
    ptl: {label: 'PTL merchants', match: (m) => !!m.isPTLMerchant},
}

const KYC_VIEWS = {
    approved:          {label: 'KYC approved',    status: 'KYC_APPROVED'},
    'not-started':     {label: 'KYC not started', status: 'KYC_PENDING'},
    'awaiting-review': {label: 'Awaiting review', status: 'KYC_UPLOADED'},
    rejected:          {label: 'Docs rejected',   status: 'KYC_REJECTED'},
}

// Fields the readiness chips need, on top of what the list already displayed.
const INCLUDES = [
    'name', 'status', 'merchantId', 'id', 'address', 'city', 'state', 'pincode',
    'country', 'merchantSignedDoc', 'codRemittanceConfig', 'createdDate',
    'is_deleted', 'companyName', 'isD2CMerchant', 'isPTLMerchant', 'isTestMerchant',
    ...READINESS_FIELDS,
].join(',')

function FilterPill({label, onClear}) {
    return (
        <Box sx={{
            ...SX.pill,
            fontSize: 11.5, px: 1, py: '4px', gap: 0.4,
            color: C.accentInk, bgcolor: C.accentSoft, border: `1px solid ${C.accentBorder}`,
        }}>
            {label}
            <Box
                component='button' type='button' onClick={onClear} aria-label={`Clear ${label} filter`}
                sx={{
                    display: 'inline-flex', alignItems: 'center', border: 'none', p: 0, ml: 0.2,
                    bgcolor: 'transparent', cursor: 'pointer', color: 'inherit', lineHeight: 1,
                    '&:hover': {color: C.danger},
                }}
            >
                <CloseIcon sx={{fontSize: 13}} />
            </Box>
        </Box>
    )
}

// The merchant-panel access flags, shown together behind one summary button.
const ACCESS_FIELDS = [
    {field: 'allocationVisibleMerchantPanel', label: 'Allocation Visibility', text: 'Allocation'},
    {field: 'rateCardVisibleMerchantPanel', label: 'Rate Card Visibility', text: 'Rate Card'},
    {field: 'channelOrdersVisibleMerchantPanel', label: 'Channel Orders Visibility', text: 'Channel Orders'},
    {field: 'returnsVisibleMerchantPanel', label: 'Returns Visibility', text: 'Returns'},
    // The ProShip Direct master switch: the menu group, its pages and /api/direct/** for the merchant.
    {field: 'isProzoDirectEnabled', label: 'ProShip Direct', text: 'ProShip Direct',
        note: 'The merchant sees the change after their next login.'},
]

// Summary + popover: the row shows a compact "N of 5" button; the toggles live in a popover so five
// switches never crowd the table row. `values` is the single source of truth (synced from the row),
// so the count and the switches never disagree; the confirm dialog is rendered OUTSIDE the popover
// (and the popover closes when it opens) so nested-modal focus issues can't make a toggle snap back.
function MerchantAccessCell({row}) {
    const [values, setValues] = useState(() => {
        const v = {}
        ACCESS_FIELDS.forEach((f) => { v[f.field] = !!row[f.field] })
        return v
    })
    // Re-sync if the row's flags arrive/change (e.g. after the async visibility fetch).
    useEffect(() => {
        const v = {}
        ACCESS_FIELDS.forEach((f) => { v[f.field] = !!row[f.field] })
        setValues(v)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [row.allocationVisibleMerchantPanel, row.rateCardVisibleMerchantPanel, row.channelOrdersVisibleMerchantPanel, row.returnsVisibleMerchantPanel, row.isProzoDirectEnabled])

    const [anchorEl, setAnchorEl] = useState(null)
    const [pending, setPending] = useState(null) // {field,label,text,next} awaiting confirmation
    const [busy, setBusy] = useState(false)
    const onCount = ACCESS_FIELDS.filter((f) => values[f.field]).length

    const requestToggle = (f, next) => { setPending({...f, next}); setAnchorEl(null) }

    const confirm = async () => {
        setBusy(true)
        try {
            await post(`${apiConstants.MERCHANT_DETAILS}/${row.id}/updatefield`, {field: pending.field, value: pending.next})
            setValues((prev) => ({...prev, [pending.field]: pending.next}))
            setPending(null)
        } catch (err) {
            console.error(err)
        } finally {
            setBusy(false)
        }
    }

    const total = ACCESS_FIELDS.length
    const open = Boolean(anchorEl)
    // Count chip tone: none = grey, all = green, some = blue.
    const countTone = onCount === 0
        ? {fg: '#637381', bg: '#EEF1F5'}
        : onCount === total
            ? {fg: '#0F7B4F', bg: '#E9F7EF'}
            : {fg: '#1565c0', bg: '#E8F1FC'}

    return (
        <>
            <Box
                role='button'
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget) }}
                sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                    height: 30, pl: 1.25, pr: 0.5, borderRadius: '999px',
                    border: '1px solid', borderColor: open ? 'primary.main' : 'divider',
                    bgcolor: open ? 'action.hover' : 'background.paper', transition: 'border-color .15s, background-color .15s',
                    '&:hover': {borderColor: 'primary.main', bgcolor: 'action.hover'},
                }}
            >
                <TuneOutlinedIcon sx={{fontSize: 15, color: 'text.secondary'}} />
                <Typography component='span' sx={{fontSize: 12.5, fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap'}}>
                    Panel access
                </Typography>
                <Box component='span' sx={{
                    display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
                    fontSize: 11, fontWeight: 800, lineHeight: 1, px: 0.75, py: '4px', borderRadius: '999px',
                    color: countTone.fg, bgcolor: countTone.bg,
                }}>
                    {onCount}/{total}
                </Box>
                <KeyboardArrowDownIcon sx={{
                    fontSize: 18, color: 'text.disabled',
                    transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s',
                }} />
            </Box>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: 'top', horizontal: 'left'}}
                PaperProps={{sx: {borderRadius: '12px', p: 1, mt: 0.5, boxShadow: '0 8px 28px rgba(20,30,55,0.14)'}}}
            >
                <Typography sx={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: 'text.secondary', px: 1, pt: 0.5, pb: 1,
                }}>
                    Merchant Panel Access
                </Typography>
                {ACCESS_FIELDS.map((f) => (
                    <Box key={f.field} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                        minWidth: 210, px: 1, py: 0.5, borderRadius: '8px', '&:hover': {bgcolor: 'action.hover'},
                    }}>
                        <Typography sx={{fontSize: 13.5, color: 'text.primary', fontWeight: 500}}>{f.text}</Typography>
                        <Switch
                            size='small'
                            color='success'
                            checked={!!values[f.field]}
                            onChange={(e) => requestToggle(f, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Box>
                ))}
            </Popover>
            <Dialog
                open={pending !== null}
                onClose={() => !busy && setPending(null)}
                onClick={(e) => e.stopPropagation()}
                maxWidth='xs'
                fullWidth
                PaperProps={{sx: {borderRadius: '14px'}}}
            >
                <DialogTitle sx={{fontWeight: 700, fontSize: 16}}>
                    {pending?.next ? 'Enable' : 'Disable'} {pending?.text}?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{fontSize: 13.5, color: '#475569', lineHeight: 1.6}}>
                        This will <b>{pending?.next ? 'show' : 'hide'}</b> the <b>{pending?.text}</b> page on the
                        merchant panel for <b>{row.name || 'this merchant'}</b>. {pending?.note || 'The change takes effect immediately.'}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{px: 3, py: 2}}>
                    <Button onClick={() => setPending(null)} disabled={busy} sx={{textTransform: 'none'}}>Cancel</Button>
                    <LoadingButton
                        loading={busy}
                        onClick={confirm}
                        variant='contained'
                        color={pending?.next ? 'primary' : 'error'}
                        sx={{textTransform: 'none', fontWeight: 700}}
                    >
                        Yes, {pending?.next ? 'enable' : 'disable'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}
const merchantPanelAccessColumn = {
    field: 'merchantPanelAccess',
    headerName: 'Merchant Panel Access',
    headerAlign: 'left', align: 'left', sortable: false,
    hideSortIcons: true, disableColumnMenu: true, minWidth: 200, flex: 1,
    renderHeader: () => (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
            <Box component='span' sx={{
                fontWeight: 600, fontSize: '11px', color: 'text.secondary',
                textTransform: 'uppercase', letterSpacing: '0.02em', lineHeight: 1.3,
            }}>
                Merchant Panel Access
            </Box>
            <Tooltip title='Show / hide these pages on the merchant panel'>
                <InfoOutlinedIcon
                    sx={{fontSize: 14, color: 'text.secondary', cursor: 'default'}}
                    onClick={(e) => e.stopPropagation()}
                />
            </Tooltip>
        </Box>
    ),
    renderCell: ({row}) => <MerchantAccessCell row={row} />,
}

export default function AllMerchants() {
    const methods = useForm({defaultValues: {merchant: ''}})
    const {reset, watch} = methods

    const [data,           setData]           = useState([])
    const [page,           setPage]           = useState(0)
    const [sizePerPage,    setSizePerPage]    = useState(20)
    const [reload,         setReload]         = useState(0)
    const [isSubmitting,   setIsSubmitting]   = useState(false)
    const [isLoading,      setIsLoading]      = useState(false)
    const [filter,         setFilter]         = useState()
    const [merchantFilter, setMerchantFilter] = useState(null)

    const [searchParams, setSearchParams] = useSearchParams()
    const segment = searchParams.get('segment')
    const kycView = searchParams.get('kyc')

    const clearParam = (key) => {
        const next = new URLSearchParams(searchParams)
        next.delete(key)
        setSearchParams(next, {replace: true})
        setPage(0)
    }

    const selectedMerchant = watch('merchant')
    useEffect(() => {setFilter(selectedMerchant)}, [selectedMerchant])

    useEffect(() => {
        if (filter) {
            setIsLoading(true)
            get(`${apiConstants.MERCHANT}/${filter}`)
                .then((res) => {
                    const m = res?.data
                    if (!isAdmin() || !m?.id) {
                        setMerchantFilter(m ? [m] : [])
                        return
                    }
                    return Promise.all([
                        post(apiConstants.MERCHANT_ALLOCATION_VISIBILITY, [m.id]),
                        post(apiConstants.MERCHANT_RATECARD_VISIBILITY, [m.id]),
                        post(apiConstants.MERCHANT_CHANNELORDERS_VISIBILITY, [m.id]),
                        post(apiConstants.MERCHANT_RETURNS_VISIBILITY, [m.id]),
                        post(apiConstants.MERCHANT_DIRECT_VISIBILITY, [m.id]),
                    ])
                        .then(([aRes, rRes, cRes, retRes, dRes]) => {
                            const aMap = aRes?.data || {}
                            const rMap = rRes?.data || {}
                            const cMap = cRes?.data || {}
                            const retMap = retRes?.data || {}
                            const dMap = dRes?.data || {}
                            setMerchantFilter([{...m,
                                allocationVisibleMerchantPanel: !!aMap[m.id],
                                rateCardVisibleMerchantPanel: !!rMap[m.id],
                                channelOrdersVisibleMerchantPanel: !!cMap[m.id],
                                returnsVisibleMerchantPanel: !!retMap[m.id],
                                isProzoDirectEnabled: !!dMap[m.id],
                            }])
                        })
                        .catch(() => setMerchantFilter([m]))
                })
                .finally(() => setIsLoading(false))
        } else {
            setIsSubmitting(true)
            // Use searchCached for faster merchant list loads.
            post(apiConstants.MERCHANT_SEARCH_CACHED, {
                offset: page * sizePerPage,
                limit: 10000,
                andfilter: [],
                includes: INCLUDES,
            })
                .then((res) => {
                    // Restored pre-2026-05-27 behaviour: show every active, non-test
                    // merchant on the Merchants Master list regardless of KYC stage.
                    // The strict `status === 'KYC_APPROVED'` filter (added in commit
                    // 0eee122a "Kyc Analytics") was meant to pair with the new
                    // dedicated KYC pages (`/merchant-config/kyc/...`) being exposed
                    // via fresh sidebar items — but those sidebar entries never
                    // landed on master, so merchants in KYC_PENDING / KYC_UPLOADED /
                    // KYC_REJECTED stages became unreachable from the sidebar and
                    // their per-document Approve/Reject actions (on the merchant
                    // detail page) effectively orphaned. Reverting just this filter
                    // unblocks the KYC review workflow on master without touching
                    // anything else.
                    const activeMerchants = (res.data?.data || []).filter(
                        (v) => v.is_deleted === '0' && !v.test_merchant
                    )
                    if (!isAdmin() || activeMerchants.length === 0) {
                        setData(activeMerchants)
                        return
                    }
                    // Merge the merchant-panel allocation-visibility flag (separate
                    // merchant_config collection) so the admin-only column can render + sort.
                    const ids = activeMerchants.map((m) => m.id)
                    return Promise.all([
                        post(apiConstants.MERCHANT_ALLOCATION_VISIBILITY, ids),
                        post(apiConstants.MERCHANT_RATECARD_VISIBILITY, ids),
                        post(apiConstants.MERCHANT_CHANNELORDERS_VISIBILITY, ids),
                        post(apiConstants.MERCHANT_RETURNS_VISIBILITY, ids),
                        post(apiConstants.MERCHANT_DIRECT_VISIBILITY, ids),
                    ])
                        .then(([aRes, rRes, cRes, retRes, dRes]) => {
                            const aMap = aRes?.data || {}
                            const rMap = rRes?.data || {}
                            const cMap = cRes?.data || {}
                            const retMap = retRes?.data || {}
                            const dMap = dRes?.data || {}
                            setData(
                                activeMerchants.map((m) => ({
                                    ...m,
                                    allocationVisibleMerchantPanel: !!aMap[m.id],
                                    rateCardVisibleMerchantPanel: !!rMap[m.id],
                                    channelOrdersVisibleMerchantPanel: !!cMap[m.id],
                                    returnsVisibleMerchantPanel: !!retMap[m.id],
                                    isProzoDirectEnabled: !!dMap[m.id],
                                }))
                            )
                        })
                        .catch(() => setData(activeMerchants))
                })
                .finally(() => setIsSubmitting(false))
        }
    }, [page, sizePerPage, reload, filter])

    // Segment / KYC narrow the list that has already been fetched -- the single
    // merchant lookup (search box) always wins, since that is an explicit pick.
    const rows = useMemo(() => {
        if (merchantFilter) return merchantFilter
        let out = data
        const seg = SEGMENTS[segment]
        if (seg) out = out.filter(seg.match)
        const kyc = KYC_VIEWS[kycView]
        if (kyc) out = out.filter((m) => (m.kycStatus || m.status) === kyc.status)
        return out
    }, [data, merchantFilter, segment, kycView])

    const activeFilters = [
        SEGMENTS[segment] && {key: 'segment', label: SEGMENTS[segment].label},
        KYC_VIEWS[kycView] && {key: 'kyc', label: KYC_VIEWS[kycView].label},
    ].filter(Boolean)

    return (
        <>
            {(isSubmitting || isLoading) && <Loader />}
            <PageTitle>{SEGMENTS[segment]?.label || KYC_VIEWS[kycView]?.label || 'Merchants'}</PageTitle>

            {/* ── Filter bar ── */}
            <Card elevation={0} sx={{...SX.card, mb: 2}}>
                <CardContent sx={{p: 2, '&:last-child': {pb: 2}}}>
                    <FormProvider methods={methods}>
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end'}}>
                            <FilterField sx={{maxWidth: 400}}>
                                <FieldLabel>Search Merchant</FieldLabel>
                                <RHFSearchableSelectField
                                    fullWidth
                                    name='merchant'
                                    label=''
                                    options={_.orderBy(data, 'name').map((v) => ({
                                        value: v.id,
                                        label: v.companyName ? `${v.name} (${v.companyName})` : v.name,
                                    }))}
                                />
                            </FilterField>

                            {/* Find any setting across Merchant Setup by name. */}
                            <FilterField sx={{maxWidth: 320}}>
                                <FieldLabel>Find a setting</FieldLabel>
                                <ConfigSearch merchantId={selectedMerchant || null} />
                            </FilterField>

                            {selectedMerchant && (
                                <Box
                                    component='button' type='button'
                                    onClick={() => {setMerchantFilter(null); setFilter(null); reset({merchant: null})}}
                                    sx={{
                                        ...SX.ctaGhost, fontSize: 12, px: 1.5, py: '6px',
                                        bgcolor: 'transparent', border: `1px solid ${C.lineStrong}`,
                                        borderRadius: '6px', cursor: 'pointer',
                                    }}
                                >
                                    Clear
                                </Box>
                            )}
                        </Box>

                        {activeFilters.length > 0 && (
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5, flexWrap: 'wrap'}}>
                                <Typography sx={TYPE.eyebrow}>Showing</Typography>
                                {activeFilters.map((f) => (
                                    <FilterPill key={f.key} label={f.label} onClear={() => clearParam(f.key)} />
                                ))}
                                <Typography sx={{...TYPE.meta, fontVariantNumeric: 'tabular-nums'}}>
                                    {rows.length} of {data.length}
                                </Typography>
                            </Box>
                        )}
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Table ── */}
            {(!isSubmitting && !isLoading && rows.length === 0) ? (
                <EmptyState
                    title={activeFilters.length ? 'No merchants match this view' : 'No merchants found'}
                    body={activeFilters.length
                        ? 'Clear a filter above to widen the list.'
                        : 'No active, non-test merchants came back. If that looks wrong, try reloading.'}
                />
            ) : (
                <MerchantDataTable
                    rows={rows}
                    page={page}
                    setPage={setPage}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    rowCount={rows.length}
                    reload={reload}
                    setReload={setReload}
                    extraColumns={isAdmin() ? [merchantPanelAccessColumn] : []}
                />
            )}
        </>
    )
}
